import { FormatEnum, reportsExportsListQueryKey } from '@crm/api'
import {
  reportsExportsRetrieveOptions,
  reportsTopProductsCategoryDistributionExportCreateMutation,
  reportsTopProductsExportCreateMutation
} from '@crm/api'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { downloadFile, showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { notification } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { MenuProps } from 'antd/lib'
import { useEffect, useState } from 'react'

type ExportType = 'top-products' | 'category-distribution'

const mutationMap = {
  'top-products': reportsTopProductsExportCreateMutation,
  'category-distribution':
    reportsTopProductsCategoryDistributionExportCreateMutation
}

export const useReportExport = (type: ExportType, params: AnyObject) => {
  const { t } = useLocales()
  const [fileId, setFileId] = useState<number | null>(null)
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { mutate } = useMutation({
    ...mutationMap[type]({}),
    onSuccess(data) {
      invalidateQueries([reportsExportsListQueryKey()])
      setFileId(data.id)
    }
  })

  const { data: exportFile } = useQuery({
    ...reportsExportsRetrieveOptions({ path: { id: fileId ?? 0 } }),
    enabled: !!fileId,
    refetchInterval: 1000
  })

  const onExport: MenuProps['onClick'] = (info) => {
    mutate({
      body: {
        format: info.key as FormatEnum,
        date_from: params.date_from,
        date_to: params.date_to
      }
    })
  }

  const dropdownItems = [
    { key: FormatEnum.CSV, label: t('reports.download_csv') },
    { key: FormatEnum.XLSX, label: t('reports.download_excel') }
  ]

  useEffect(() => {
    if (!exportFile) return

    if (exportFile.status === 'done' && exportFile.attachment?.file) {
      try {
        downloadFile(exportFile.attachment.file, exportFile.attachment.title)

        notification.success({
          message: t('success.success'),
          description: t('success.export_started')
        })
      } catch (error) {
        showApiErrors(error)
      }
      setFileId(null)
    }
  }, [exportFile, t])

  return { onExport, exportFile, dropdownItems }
}
